/**
 * PublicHeader — PHASE 8 APEX (B2)
 *
 * Header public adaptatif :
 *   • Mode « dark-glass » : en haut de page, si la page expose un hero sombre
 *     (attribut `data-hero-dark` posé par la page), le header est transparent
 *     sur le navy — logo variant dark, liens ivoire. Supprime le « bandeau
 *     grisâtre » audité (AUDIT_APEX_PHASE8_PUBLIC.md §4.1).
 *   • Mode « light » : dès le scroll (ou sur les pages claires), fond ivoire
 *     translucide + blur, logo variant light.
 *   • Logo responsive : `md` en desktop, `sm` en mobile (le logo md débordait
 *     de la barre en 390px).
 *   • Palette : tokens gold/navy canoniques (zéro hex inline).
 */
import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { List, X, ArrowRight } from '@phosphor-icons/react';
import { useI18n } from '../context/I18nContext';
import { localizedPath } from '../i18n/routes';
import { isPublicSpvOfferEnabled } from '../config/featureFlags';
import LanguageSwitcher from './LanguageSwitcher';
import WMLogo from './brand/WMLogo';

export default function PublicHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [darkHero, setDarkHero] = useState(false);
  const location = useLocation();
  const { t, lang } = useI18n();

  // Scroll : bascule le header en mode solide dès 32px.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Détection du hero sombre de la page courante (les pages le déclarent
  // via `data-hero-dark`). MutationObserver : robuste face au lazy-loading
  // des pages (le hero peut monter bien après la navigation).
  useEffect(() => {
    const check = () => setDarkHero(!!document.querySelector('[data-hero-dark]'));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [location.pathname]);

  const isDark = darkHero && !scrolled && !mobileMenuOpen;

  const navLinks = [
    { pageKey: 'home',       label: t('nav.home') },
    { pageKey: 'about',      label: t('nav.about') },
    // DIRECTIVE PO (06/09/2026) — « Notre équipe » en onglet dédié, juste
    // après « À propos » (élément de confiance avant l'acquisition).
    { pageKey: 'team',       label: lang === 'fr' ? 'Notre équipe' : 'Our team' },
    { pageKey: 'howItWorks', label: t('nav.howItWorks') },
    { pageKey: 'realisations', label: lang === 'fr' ? 'Réalisations' : 'Track record' },
    { pageKey: 'journal',    label: lang === 'fr' ? 'Journal' : 'Journal' },
    ...(isPublicSpvOfferEnabled()
      ? [{ pageKey: 'createSpv', label: lang === 'fr' ? 'Créer une SPV' : 'Create an SPV' }]
      : []),
    { pageKey: 'contact',    label: t('nav.contact') },
  ];
  // HARDENING V1 (LOT 3 — P0-01) : en desktop, « Accueil » est porté par le
  // logo (pattern institutionnel) — la nav horizontale gagne la place qui
  // faisait déborder le CTA « Demander l'accès » hors écran à 1440 px.
  // Le menu replié conserve l'item « Accueil ».
  const desktopNavLinks = navLinks.filter(l => l.pageKey !== 'home');

  const requestAccessPath = localizedPath('requestAccess', lang);
  const loginPath = localizedPath('login', lang);

  const isActivePage = (pageKey) => {
    const target = localizedPath(pageKey, lang);
    const current = location.pathname.replace(/\/$/, '') || '/';
    return current === target;
  };

  const linkBase = 'relative whitespace-nowrap text-[11.5px] 2xl:text-[12.5px] tracking-[0.03em] uppercase font-medium transition-colors duration-200 py-1';
  const linkIdle = isDark ? 'text-ivory/90 hover:text-gold' : 'text-graphite hover:text-navy-deep';
  const linkActive = isDark ? 'text-ivory' : 'text-navy-deep';

  return (
    <header
      className={`wm-no-print public-header fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        isDark
          ? 'bg-navy-950/25 [backdrop-filter:blur(10px)] border-b border-ivory/10'
          : 'bg-ivory/90 [backdrop-filter:saturate(140%)_blur(18px)] border-b border-charcoal/5 shadow-[0_6px_20px_-14px_rgba(14,15,18,0.10)]'
      }`}
      data-testid="public-header"
      data-header-mode={isDark ? 'dark' : 'light'}
      data-lot="phase8-brand-experience"
    >
      {/* HARDENING V1 (P0-01) — le header a son propre gabarit : paddings
          réduits vs container-immersive pour garantir le CTA à l'écran.
          DIRECTIVE PO (06/09/2026) — micro-optimisation xl (gap-3 nav,
          11.5px liens) : l'onglet « Notre équipe » ajouté faisait déborder
          le CTA de 54px à 1280 px. Aucun redesign, 2xl inchangé. */}
      <div className="max-w-[1680px] mx-auto w-full px-6 lg:px-8 2xl:px-16 py-3.5 flex items-center justify-between gap-3 2xl:gap-4">
        <Link
          to={localizedPath('home', lang)}
          className="shrink-0 transition-opacity duration-200 hover:opacity-90"
          data-testid="logo-link"
          aria-label="Wealth Management — Global Real Assets"
        >
          {/* Logo complet (353px) réservé aux très grands écrans ; version
              compacte ailleurs — c'était le premier poste du débordement.
              DIRECTIVE PO (06/09/2026) — seuil relevé de 2xl (1536) à
              1680px : avec l'onglet « Notre équipe », le logo complet
              faisait coller le CTA au bord entre 1536 et 1650 px. */}
          <span className="hidden min-[1680px]:block">
            <WMLogo variant={isDark ? 'dark' : 'light'} mode="horizontal" size="md" />
          </span>
          <span className="min-[1680px]:hidden">
            <WMLogo variant={isDark ? 'dark' : 'light'} mode="horizontal" size="sm" />
          </span>
        </Link>

        {/* Nav complète à partir de xl (1280) : en dessous, elle ne tient pas
            physiquement (~1350px requis) et poussait le CTA hors écran. */}
        <nav className="hidden xl:flex items-center gap-2 2xl:gap-5" data-testid="public-nav">
          {desktopNavLinks.map(link => {
            const active = isActivePage(link.pageKey);
            return (
              <Link
                key={link.pageKey}
                to={localizedPath(link.pageKey, lang)}
                className={`${linkBase} ${active ? linkActive : linkIdle}`}
                data-testid={`nav-${link.pageKey}`}
              >
                {link.label}
                {active && (
                  <span className="absolute left-0 right-0 -bottom-1 h-[2px] bg-gold" aria-hidden />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="hidden xl:flex items-center gap-2 shrink-0">
          <LanguageSwitcher context="public" />
          <Link
            to={loginPath}
            className={`inline-flex items-center px-2 py-2 text-[12px] font-medium tracking-[0.04em] uppercase transition-colors duration-200 ${linkIdle}`}
            data-testid="login-link"
          >
            {t('common.signIn')}
          </Link>
          <Link
            to={requestAccessPath}
            className="inline-flex items-center gap-2 px-3.5 2xl:px-5 py-2.5 text-[12px] 2xl:text-[12.5px] font-medium tracking-[0.04em] uppercase text-ivory bg-gold-dark hover:bg-gold-dark/90 transition-[background-color,transform] duration-200 hover:-translate-y-px whitespace-nowrap"
            data-testid="request-access-btn"
          >
            {t('common.requestAccess')}
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <div className="xl:hidden flex items-center gap-2">
          <LanguageSwitcher context="public" />
          {/* Conversion préservée sous xl : CTA compact toujours à l'écran
              (tablettes / petits laptops), avant le menu replié. */}
          <Link
            to={requestAccessPath}
            className="hidden md:inline-flex items-center px-3.5 py-2 text-[11.5px] font-medium tracking-[0.04em] uppercase text-ivory bg-gold-dark hover:bg-gold-dark/90 transition-colors duration-200 whitespace-nowrap"
            data-testid="request-access-btn-compact"
          >
            {t('common.requestAccess')}
          </Link>
          <button
            className={`p-2 ${isDark ? 'text-ivory' : 'text-charcoal'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="xl:hidden bg-ivory border-t border-stone" data-testid="mobile-menu">
          <nav className="px-6 py-4 flex flex-col gap-3">
            {navLinks.map(link => {
              const active = isActivePage(link.pageKey);
              return (
                <Link
                  key={link.pageKey}
                  to={localizedPath(link.pageKey, lang)}
                  className={`py-2 text-sm tracking-[0.04em] uppercase font-medium ${active ? 'text-navy-deep' : 'text-graphite'}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                  {active && <span className="ml-2 inline-block w-4 h-px align-middle bg-gold" />}
                </Link>
              );
            })}
            <div className="border-t border-stone pt-4 mt-2 flex flex-col gap-3">
              <Link to={loginPath} className="text-sm py-2 tracking-[0.04em] uppercase text-graphite" onClick={() => setMobileMenuOpen(false)}>
                {t('common.signIn')}
              </Link>
              <Link
                to={requestAccessPath}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium tracking-[0.04em] uppercase text-ivory bg-gold-dark"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('common.requestAccess')}
                <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
